import { TriangleAlert } from "lucide-react";
import { Link } from "react-router";

import type { PatientResponseDto } from "~/lib/api";

interface OverdueItem {
  patientId: string;
  patientName: string;
  bedNumber: string;
  description: string;
}

/** Flatten patients → their overdue urgent tasks (notifiedAt stamped by HAN-78). */
function collectOverdue(patients: PatientResponseDto[]): OverdueItem[] {
  const items: OverdueItem[] = [];
  for (const p of patients) {
    // HAN-78: an acknowledged patient is signed-off; its tasks are no longer
    // actionable (and the backend scan excludes them), so they never count as
    // overdue here even if notified_at was stamped before acknowledgement.
    if (p.acknowledgedAt != null) continue;
    for (const t of p.tasks) {
      if (t.urgency === "urgent" && t.completedAt == null && t.notifiedAt != null) {
        items.push({
          patientId: p.id,
          patientName: p.fullName,
          bedNumber: p.bedNumber,
          description: t.description,
        });
      }
    }
  }
  return items;
}

/**
 * HAN-78 (V2-E5.1) top-of-screen safety banner. Lists every urgent task the
 * overdue scan has flagged (`notifiedAt` set) across the session's patients,
 * each linking to its patient. Renders nothing when there are none. Sticky +
 * red so it can't be missed; role="alert" / aria-live="assertive" announces it
 * immediately to AT (safety-critical — polite/status would allow suppression).
 */
export function OverdueTasksBanner({
  sessionId,
  patients,
}: {
  sessionId: string;
  patients: PatientResponseDto[];
}): JSX.Element | null {
  const overdue = collectOverdue(patients);
  if (overdue.length === 0) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-label={`${overdue.length} urgent task${overdue.length > 1 ? "s" : ""} overdue`}
      data-testid="overdue-tasks-banner"
      className="sticky top-0 z-20 -mx-4 border-b border-red-700 bg-red-600 px-4 py-3 text-sm text-white sm:-mx-6 sm:px-6"
    >
      <div className="flex items-center gap-2 font-semibold">
        <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
        {overdue.length} urgent task{overdue.length > 1 ? "s" : ""} overdue
      </div>
      <ul className="mt-1.5 space-y-1">
        {overdue.map((item, i) => (
          <li key={`${item.patientId}-${i}`}>
            <Link
              to={`/session/${sessionId}/patient/${item.patientId}`}
              className="underline-offset-2 hover:underline"
            >
              {item.patientName}, bed {item.bedNumber} — {item.description}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
